import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const rp = (n) => "Rp" + (n == null ? "0" : Math.round(n).toLocaleString("id-ID"));
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const STATUS_TONE = {
  "Draft": "bg-slate-700/40 text-slate-300",
  "Menunggu Persetujuan": "bg-amber-500/15 text-amber-400",
  "Disetujui": "bg-emerald-500/15 text-emerald-400",
  "Nonaktif": "bg-slate-800 text-slate-500",
};

export default function Budget() {
  const { can } = useAuth();
  const canManage = can("budget.manage");
  const canApprove = can("budget.approve");
  const [tab, setTab] = useState("budget");
  const [budgets, setBudgets] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [detail, setDetail] = useState(null); // budget being viewed/edited
  const [editLines, setEditLines] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ year: String(new Date().getFullYear()), name: "" });
  // add-line form
  const [al, setAl] = useState({ account_code: "", monthly: "" });

  const loadBudgets = useCallback(async () => {
    try { setBudgets((await api.get("/finance/budget")).data); } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => {
    loadBudgets();
    api.get("/accounting/chart-of-accounts")
      .then((r) => setAccounts(r.data.filter((a) => a.allow_posting && ["REVENUE", "COGS", "EXPENSE", "OTHER"].includes(a.type))))
      .catch(() => {});
  }, [loadBudgets]);

  const openDetail = async (id) => {
    try {
      const b = (await api.get(`/finance/budget/${id}`)).data;
      setDetail(b);
      setEditLines((b.lines || []).map((l) => ({ ...l, amounts: { ...l.amounts } })));
    } catch (e) { toast.error(apiError(e)); }
  };

  const createBudget = async () => {
    if (!nf.name.trim()) return toast.error("Nama budget wajib diisi");
    try {
      const b = (await api.post("/finance/budget", { year: Number(nf.year), name: nf.name, lines: [] })).data;
      toast.success(`Budget ${b.name} dibuat (Draft)`);
      setShowNew(false); setNf({ year: String(new Date().getFullYear()), name: "" });
      await loadBudgets(); openDetail(b.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addLine = () => {
    if (!al.account_code) return toast.error("Pilih akun");
    if (editLines.find((l) => l.account_code === al.account_code)) return toast.error("Akun sudah ada di budget");
    const acc = accounts.find((a) => a.code === al.account_code);
    const m = Number(al.monthly || 0);
    const amounts = {};
    if (m) for (let i = 1; i <= 12; i++) amounts[String(i)] = m;
    setEditLines([...editLines, { account_code: acc.code, account_name: acc.name, account_type: acc.type, amounts }]);
    setAl({ account_code: "", monthly: "" });
  };

  const setMonth = (idx, mo, val) => {
    const next = [...editLines];
    next[idx] = { ...next[idx], amounts: { ...next[idx].amounts, [String(mo)]: Number(val || 0) } };
    setEditLines(next);
  };
  const removeLine = (idx) => setEditLines(editLines.filter((_, i) => i !== idx));
  const lineTotal = (l) => Object.values(l.amounts || {}).reduce((s, v) => s + Number(v || 0), 0);

  const saveLines = async () => {
    try {
      await api.put(`/finance/budget/${detail.id}`, {
        year: detail.year, name: detail.name,
        lines: editLines.map((l) => ({ account_code: l.account_code, amounts: l.amounts })),
      });
      toast.success("Budget disimpan");
      await loadBudgets(); openDetail(detail.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (path, id, ok, body) => {
    try { await api.post(`/finance/budget/${id}/${path}`, body || {}); toast.success(ok); await loadBudgets(); if (detail?.id === id) openDetail(id); }
    catch (e) { toast.error(apiError(e)); }
  };
  const reject = (id) => { const reason = prompt("Alasan penolakan (opsional):") ?? ""; act("reject", id, "Budget dikembalikan ke Draft", { reason }); };

  const editable = detail && ["Draft", "Menunggu Persetujuan"].includes(detail.status) && canManage;

  return (
    <div className="p-6" data-testid="budget-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Budget & Budget vs Aktual</h1>
          <p className="text-slate-400 text-sm">Perencanaan anggaran tahunan. Budget tidak membuat jurnal. Aktual dihitung dari jurnal Posted.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="tab-budget" onClick={() => { setTab("budget"); setDetail(null); }} className={`px-3 py-2 rounded text-sm ${tab === "budget" ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-300"}`}>Anggaran</button>
          <button data-testid="tab-vs-actual" onClick={() => setTab("vs")} className={`px-3 py-2 rounded text-sm ${tab === "vs" ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-300"}`}>Budget vs Aktual</button>
        </div>
      </div>

      {tab === "budget" && !detail && (
        <>
          <div className="flex justify-end mb-3">
            {canManage && <Button data-testid="new-budget-btn" onClick={() => setShowNew(!showNew)} className="bg-indigo-600 hover:bg-indigo-500">+ Budget Baru</Button>}
          </div>
          {showNew && (
            <Card className="p-4 bg-slate-900 border-slate-800 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div><label className="text-xs text-slate-400">Tahun</label><Input data-testid="budget-year" type="number" value={nf.year} onChange={(e) => setNf({ ...nf, year: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                <div><label className="text-xs text-slate-400">Nama Budget</label><Input data-testid="budget-name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Anggaran Operasional 2026" className="bg-slate-800 border-slate-700 text-white" /></div>
                <Button data-testid="save-budget-btn" onClick={createBudget} className="bg-emerald-600 hover:bg-emerald-500">Simpan</Button>
              </div>
            </Card>
          )}
          <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
            <table className="w-full text-sm text-slate-300">
              <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">Tahun</th><th>Nama</th><th>Versi</th><th>Total Anggaran</th><th>Status</th><th>Dibuat</th><th>Aksi</th></tr></thead>
              <tbody>
                {budgets.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500">Belum ada budget.</td></tr>}
                {budgets.map((b) => (
                  <tr key={b.id} data-testid={`budget-row-${b.id}`} className="border-b border-slate-800">
                    <td className="p-3">{b.year}</td><td>{b.name}</td><td>v{b.version}</td><td>{rp(b.total_annual)}</td>
                    <td><span className={`text-xs px-2 py-1 rounded ${STATUS_TONE[b.status] || "bg-slate-700/40"}`}>{b.status}</span></td>
                    <td className="text-xs text-slate-500">{b.created_by_name}</td>
                    <td><Button data-testid={`open-budget-${b.id}`} onClick={() => openDetail(b.id)} className="h-7 px-2 bg-slate-700 hover:bg-slate-600 text-xs">Buka</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === "budget" && detail && (
        <Card className="p-4 bg-slate-900 border-slate-800" data-testid="budget-detail">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-white font-semibold">{detail.name} · {detail.year} · v{detail.version}</h3>
              <span className={`text-xs px-2 py-1 rounded ${STATUS_TONE[detail.status]}`}>{detail.status}</span>
              {detail.approved_by && <span className="text-xs text-slate-500 ml-2">Disetujui: {detail.approved_by}</span>}
              {detail.reject_reason && <span className="text-xs text-rose-400 ml-2">Ditolak: {detail.reject_reason}</span>}
            </div>
            <div className="flex gap-2">
              {detail.status === "Draft" && canManage && <Button data-testid="submit-budget-btn" onClick={() => act("submit", detail.id, "Budget diajukan untuk persetujuan")} className="h-8 px-3 bg-amber-600 hover:bg-amber-500 text-xs">Ajukan Persetujuan</Button>}
              {detail.status === "Menunggu Persetujuan" && canApprove && <Button data-testid="approve-budget-btn" onClick={() => act("approve", detail.id, "Budget disetujui")} className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs">Setujui</Button>}
              {detail.status === "Menunggu Persetujuan" && canApprove && <Button data-testid="reject-budget-btn" onClick={() => reject(detail.id)} className="h-8 px-3 bg-rose-600 hover:bg-rose-500 text-xs">Tolak</Button>}
              <Button onClick={() => setDetail(null)} className="h-8 px-3 bg-slate-700 hover:bg-slate-600 text-xs">Kembali</Button>
            </div>
          </div>

          {editable && (
            <div className="flex flex-wrap items-end gap-2 mb-3 p-3 rounded bg-slate-800/50">
              <div><label className="text-xs text-slate-400">Akun</label>
                <select data-testid="line-account" value={al.account_code} onChange={(e) => setAl({ ...al, account_code: e.target.value })} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white w-64">
                  <option value="">Pilih akun…</option>
                  {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name} ({a.type})</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-400">Nominal / bulan</label><Input data-testid="line-monthly" type="number" value={al.monthly} onChange={(e) => setAl({ ...al, monthly: e.target.value })} className="bg-slate-800 border-slate-700 text-white w-40" /></div>
              <Button data-testid="add-line-btn" onClick={addLine} className="h-9 px-3 bg-indigo-600 hover:bg-indigo-500 text-xs">+ Tambah Baris</Button>
              <Button data-testid="save-lines-btn" onClick={saveLines} className="h-9 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs">Simpan Perubahan</Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-slate-300">
              <thead><tr className="text-left text-slate-500 border-b border-slate-800">
                <th className="p-2 sticky left-0 bg-slate-900">Akun</th>
                {MONTHS.map((m) => <th key={m} className="p-2 text-right">{m}</th>)}
                <th className="p-2 text-right">Total</th>{editable && <th></th>}
              </tr></thead>
              <tbody>
                {editLines.length === 0 && <tr><td colSpan="15" className="p-6 text-center text-slate-500">Belum ada baris anggaran.</td></tr>}
                {editLines.map((l, idx) => (
                  <tr key={l.account_code} data-testid={`line-row-${l.account_code}`} className="border-b border-slate-800">
                    <td className="p-2 sticky left-0 bg-slate-900 whitespace-nowrap">{l.account_code} · {l.account_name}</td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className="p-1 text-right">
                        {editable ? (
                          <input type="number" value={l.amounts[String(i + 1)] || ""} onChange={(e) => setMonth(idx, i + 1, e.target.value)} className="w-20 bg-slate-800 border border-slate-700 rounded px-1 py-1 text-right text-white" />
                        ) : (Number(l.amounts[String(i + 1)] || 0)).toLocaleString("id-ID")}
                      </td>
                    ))}
                    <td className="p-2 text-right font-semibold text-white">{rp(lineTotal(l))}</td>
                    {editable && <td className="p-1"><button onClick={() => removeLine(idx)} className="text-rose-400 text-xs">Hapus</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "vs" && <BudgetVsActual />}
    </div>
  );
}

function BudgetVsActual() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [from, setFrom] = useState("1");
  const [to, setTo] = useState("12");
  const [data, setData] = useState(null);
  const load = async () => {
    try { setData((await api.get(`/finance/budget/vs-actual/report?year=${year}&from_month=${from}&to_month=${to}`)).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, from, to]);
  const MO = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

  return (
    <div data-testid="budget-vs-actual">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="text-xs text-slate-400">Tahun</label><Input data-testid="vs-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} className="bg-slate-800 border-slate-700 text-white w-28" /></div>
        <div><label className="text-xs text-slate-400">Dari Bulan</label>
          <select data-testid="vs-from" value={from} onChange={(e) => setFrom(e.target.value)} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white">{MO.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></div>
        <div><label className="text-xs text-slate-400">Sampai Bulan</label>
          <select data-testid="vs-to" value={to} onChange={(e) => setTo(e.target.value)} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white">{MO.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></div>
      </div>
      {!data ? <div className="text-slate-400">Memuat…</div> : !data.has_budget ? (
        <Card className="p-6 bg-slate-900 border-slate-800 text-center text-slate-400" data-testid="vs-no-budget">{data.message}</Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Object.entries(data.totals).map(([t, g]) => (
              <Card key={t} className="p-4 bg-slate-900 border-slate-800" data-testid={`vs-total-${t}`}>
                <div className="text-xs text-slate-400">{t}</div>
                <div className="text-xs text-slate-500 mt-1">Anggaran {rp(g.budget)}</div>
                <div className="text-sm text-white">Aktual {rp(g.actual)}</div>
                <div className={`text-xs mt-1 ${g.variance >= 0 ? "text-emerald-400" : "text-rose-400"}`}>Selisih {rp(g.variance)}</div>
              </Card>
            ))}
          </div>
          <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
            <table className="w-full text-sm text-slate-300">
              <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">Akun</th><th>Tipe</th><th className="text-right">Anggaran</th><th className="text-right">Aktual</th><th className="text-right">Selisih</th><th className="text-right">%</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.account_code} data-testid={`vs-row-${r.account_code}`} className="border-b border-slate-800">
                    <td className="p-3">{r.account_code} · {r.account_name}</td>
                    <td className="text-xs text-slate-500">{r.account_type}</td>
                    <td className="text-right">{rp(r.budget)}</td>
                    <td className="text-right">{rp(r.actual)}</td>
                    <td className={`text-right ${r.favorable ? "text-emerald-400" : "text-rose-400"}`}>{rp(r.variance)}</td>
                    <td className={`text-right ${r.favorable ? "text-emerald-400" : "text-rose-400"}`}>{r.variance_pct == null ? "—" : `${r.variance_pct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
